import { useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';

export interface VizColors {
  series1: string;
  series2: string;
  series3: string;
  band: string;
  grid: string;
  axis: string;
  ink: string;
  muted: string;
  surface: string;
  /**
   * Categorical hues in fixed order.
   *
   * Indexed by the entity's position in a stably-sorted list, never by its rank
   * in the current view. If colour followed rank, filtering out the leader
   * would repaint every remaining series and the reader would have to re-learn
   * the legend on every interaction.
   */
  categorical: string[];
  /** The colour for everything folded into "Other". Deliberately not a hue. */
  other: string;
  /** Sequential ramp, low to high magnitude. One hue — never a rainbow. */
  sequential: string[];
}

type ScalarRole = Exclude<keyof VizColors, 'categorical' | 'sequential'>;

const ROLES: Record<ScalarRole, string> = {
  series1: '--series-1',
  series2: '--series-2',
  series3: '--series-3',
  band: '--band',
  grid: '--grid',
  axis: '--axis',
  ink: '--ink',
  muted: '--ink-muted',
  surface: '--surface',
  other: '--cat-other',
};

const CATEGORICAL_SLOTS = 7;
const SEQUENTIAL_STEPS = 5;

function read(): VizColors {
  const style = getComputedStyle(document.documentElement);
  const out = {} as VizColors;

  for (const [key, property] of Object.entries(ROLES)) {
    out[key as ScalarRole] = style.getPropertyValue(property).trim();
  }

  out.categorical = Array.from({ length: CATEGORICAL_SLOTS }, (_, i) =>
    style.getPropertyValue(`--cat-${i + 1}`).trim(),
  );
  out.sequential = Array.from({ length: SEQUENTIAL_STEPS }, (_, i) =>
    style.getPropertyValue(`--seq-${i + 1}`).trim(),
  );

  return out;
}

/**
 * The colour for a category at a fixed position.
 *
 * Past the last slot everything is `other` rather than a cycled hue. Cycling
 * would give slots 1 and 8 the same colour in the same chart, which is not a
 * subtle legibility issue — it is two different things drawn identically.
 */
export function categoryColor(colors: VizColors, index: number): string {
  return colors.categorical[index] ?? colors.other;
}

/**
 * Pick a step from the sequential ramp.
 *
 * `null` is the caller's signal for "no data here" and must not resolve to the
 * ramp's lowest step — an unmeasured cell and a measured zero are different
 * facts, and the ramp has no step that means "absent".
 */
export function rampColor(
  colors: VizColors,
  value: number | null,
  min: number,
  max: number,
): string | null {
  if (value === null) return null;
  if (colors.sequential.length === 0) return null;

  const span = max - min;
  const position = span === 0 ? 1 : (value - min) / span;
  const step = Math.min(colors.sequential.length - 1, Math.max(0, Math.round(position * (colors.sequential.length - 1))));

  return colors.sequential[step] ?? null;
}

/**
 * Resolve the chart palette from CSS custom properties.
 *
 * Recharts writes colours as SVG presentation attributes, where `var(...)` is
 * not resolved — passing the token string directly yields black marks. Reading
 * the computed values keeps `theme.css` the single definition of the palette
 * instead of maintaining a second copy in TypeScript that silently drifts.
 */
export function useVizColors(mode: ThemeMode): VizColors {
  const [colors, setColors] = useState<VizColors>(() =>
    typeof window === 'undefined'
      ? ({} as VizColors)
      : read(),
  );

  useEffect(() => {
    // The custom properties are applied by the same effect that stamps
    // data-theme; re-read on the next frame so we see the new values.
    const id = requestAnimationFrame(() => setColors(read()));
    return () => cancelAnimationFrame(id);
  }, [mode]);

  useEffect(() => {
    if (mode !== 'system') return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => requestAnimationFrame(() => setColors(read()));
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
  }, [mode]);

  return colors;
}

export function useThemeMode(): [ThemeMode, (mode: ThemeMode) => void] {
  const [mode, setMode] = useState<ThemeMode>(
    () => (localStorage.getItem('dv-theme') as ThemeMode | null) ?? 'system',
  );

  useEffect(() => {
    localStorage.setItem('dv-theme', mode);
    if (mode === 'system') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  return [mode, setMode];
}
